import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, Renderer2, inject, input } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[cmg-ssn-mask]',
})
export class CmgPrimeNgSsnMaskDirective implements AfterViewInit, OnDestroy {
  private readonly _el = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);

  readonly formControl = input<AbstractControl>(undefined);
  private _fullSSN: string = '';
  private _sub: Subscription;
  private _isFocused: boolean;

  ngAfterViewInit() {
    this._fullSSN = this._el.nativeElement.value ?? '';
    this._setObfuscatedValue();

    this._sub = this.formControl().valueChanges.subscribe((value) => {
      if (!this._isFocused) {
        //Process the initial load of the ssn before any focus on the input.
        this._processValue(value ?? '');
        this._setObfuscatedValue();
      }
    });
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  @HostListener('focus')
  onFocus(): void {
    this._isFocused = true;
    this._setOriginalValue();
  }

  @HostListener('blur')
  onBlur(): void {
    this._isFocused = false;
    this.formControl().setValue(this._fullSSN);
    this.formControl().markAsDirty();
    this.formControl().markAsTouched();
    this._setObfuscatedValue();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this._processValue(input.value ?? '');
  }

  private _processValue(value: string | undefined): void {
    const input = this._el.nativeElement as HTMLInputElement;
    this._fullSSN = value?.replace(/\D/g, ''); // Remove all non-digit characters
    if (this._fullSSN?.length > 9) {
      this._fullSSN = this._fullSSN.slice(0, 9); // Limit input to 9 digits
    }
    input.value = this._formatSsn(this._fullSSN);
    this.formControl().markAsDirty();
  }

  private _setObfuscatedValue() {
    if (this._fullSSN?.length <= 4) {
      return 'x'.repeat(this._fullSSN.length);
    }
    const visibleSection = this._fullSSN?.slice(-4) ?? 'xxxx';
    const obfuscated = `xxx-xx-${visibleSection}`;
    this._renderer.setProperty(this._el.nativeElement, 'value', obfuscated);
  }

  private _setOriginalValue() {
    const valueToDisplay = this._fullSSN ? this._formatSsn(this._fullSSN) : '';
    this._renderer.setProperty(this._el.nativeElement, 'value', valueToDisplay);
  }

  private _formatSsn(ssn: string): string {
    ssn = ssn?.replace(/\D/g, '');
    if (ssn?.length > 5) {
      return ssn.replace(/(\d{3})(\d{2})(\d{0,4})/, '$1-$2-$3');
    } else if (ssn?.length > 3) {
      return ssn.replace(/(\d{3})(\d{0,2})/, '$1-$2');
    } else {
      return ssn;
    }
  }
}
